import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { ViewToggle } from '../../components/ViewToggle';
import { ExportMenu } from '../accounting/ExportMenu';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { IcpMassPickDetail } from './IcpMassPickDetail';
import { blankRow, type MassFlag, type MassPickRow, type TestMethodPair } from './icpMassPickModel';
import './module.css';

const BASE = '/api/v1/icpms-mass-picks';
const PAGE_ID = 'set-icpmsmasspick';

interface ParameterOption { id: string; name: string }

/**
 * Settings → Utility → e-Data Transfer → ICP-MS Mass Pick.
 *
 * Two views (owner request 2026-09-28): the LIST View is READ-ONLY — it shows the
 * rows only, with no inline editing, no add, and no batch commit bar. ALL editing and
 * adding happen in the DETAIL View (IcpMassPickDetail), the per-record editor reached
 * by New / Edit / row double-click / the shared <ViewToggle>. Delete acts on the list
 * selection and is applied immediately through the atomic /batch endpoint.
 *
 * (ALPACA ran this screen as a DevExpress batch-edit grid; AI keeps that server model
 * — one atomic /batch save — but the editing UI now lives in the detail form, so the
 * list is a clean read-only view like every other List/Detail form.)
 *
 * Built from the shared components (standing rules 7-9): FormTitle, AppToolbar,
 * AppDataGrid, GridSearch, ViewToggle, useNotify, useConfirm, useUnsavedGuard.
 */
export function IcpMassPickPage() {
  const notify = useNotify();
  const { confirm } = useConfirm();

  const [rows, setRows] = useState<MassPickRow[]>([]);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [parameters, setParameters] = useState<ParameterOption[]>([]);
  const [pairs, setPairs] = useState<TestMethodPair[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  // List ⇄ Detail. detailRowId = the row Oid being edited, or null for a new row;
  // detailKey remounts the editor on New / Save & New.
  const [mode, setMode] = useState<'list' | 'detail'>('list');
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  const [detailKey, setDetailKey] = useState(0);
  const [detailDirty, setDetailDirty] = useState(false);
  // Detail opens read-only ('view', Edit/Close) when just viewing a record, and
  // editable ('edit', Save trio) for New / Edit (standing rule 12).
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  // Grid maximize (zoom), driven from the search-row button (controlled AppDataGrid).
  const [gridMax, setGridMax] = useState(false);

  // Guard menu / sign-out / browser navigation while the detail draft is dirty
  // (standing rule 15). The list is read-only, so it is never itself dirty.
  useUnsavedGuard(detailDirty);

  const load = useCallback(async () => {
    const started = performance.now();
    setLoading(true);
    try {
      const [list, params, tmPairs] = await Promise.all([
        api.get<{ data?: Record<string, unknown>[] }>(BASE),
        api.get<{ data?: ParameterOption[] }>(`${BASE}/parameters`),
        api.get<{ data?: TestMethodPair[] }>(`${BASE}/test-methods`),
      ]);
      const loaded: MassPickRow[] = (list.data?.data ?? []).map((r) => ({
        id: String(r.oid ?? ''),
        isNew: false,
        parameter: r.parameter == null ? '' : String(r.parameter),
        parameterName: (r.parameterName as string) ?? '',
        element: (r.element as string) ?? '',
        method: (r.method as string) ?? '',
        matrix: (r.matrix as string) ?? '',
        testMethodKeys: (r.testMethodKeys as string) ?? '',
        // A NULL column reads as 0, exactly as ALPACA's int properties do.
        selectMass1: !!r.selectMass1, mass1: (r.mass1 as number) ?? 0,
        selectMass2: !!r.selectMass2, mass2: (r.mass2 as number) ?? 0,
        selectMass3: !!r.selectMass3, mass3: (r.mass3 as number) ?? 0,
        selectMass4: !!r.selectMass4, mass4: (r.mass4 as number) ?? 0,
      }));
      setParameters(params.data?.data ?? []);
      setPairs(tmPairs.data?.data ?? []);
      setRows(loaded);
      setSelected(new Set());
    } finally {
      setLoading(false);
      setLoadMs(performance.now() - started);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Esc restores from maximize (the page owns the maximize, not the grid).
  useEffect(() => {
    if (!gridMax) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setGridMax(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [gridMax]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.parameterName, r.element, r.method, r.matrix]
        .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  // ── List ⇄ Detail wiring ──
  const openView = (id: string) => { setDetailMode('view'); setDetailRowId(id); setDetailKey((k) => k + 1); setMode('detail'); };
  const openEdit = (id: string) => { setDetailMode('edit'); setDetailRowId(id); setDetailKey((k) => k + 1); setMode('detail'); };
  const openNew = () => { setDetailMode('edit'); setDetailRowId(null); setDetailKey((k) => k + 1); setMode('detail'); };

  const editSelected = () => {
    if (selected.size !== 1) { notify.warning('Select exactly one row to edit.'); return; }
    openEdit(String([...selected][0]));
  };

  /** ViewToggle → Detail: open the selected (or first) row READ-ONLY, else a new row. */
  const toggleToDetail = () => {
    const sel = selected.size === 1 ? String([...selected][0]) : null;
    const target = sel ?? visible[0]?.id ?? null;
    if (target) openView(target); else openNew();
  };

  const closeDetail = async () => {
    if (detailDirty) {
      const ok = await confirm({
        title: 'Unsaved changes',
        message: 'Unsaved changes will be lost. Do you want to proceed?',
      });
      if (!ok) return;
    }
    setDetailDirty(false);
    setMode('list');
  };

  const handleDetailSaved = async (after: 'new' | 'close') => {
    setDetailDirty(false);
    await load();
    if (after === 'close') { setMode('list'); return; }
    // Save & New: a fresh blank in edit mode.
    setDetailMode('edit');
    setDetailRowId(null);
    setDetailKey((k) => k + 1);
  };

  const removeSelected = async () => {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: 'Delete',
      message: `Delete ${selected.size} mass pick row${selected.size === 1 ? '' : 's'}?`,
      tone: 'danger',
    });
    if (!ok) return;
    try {
      // Applied immediately through the atomic batch endpoint (deletes only).
      await api.post(`${BASE}/batch`, { rows: [], deletes: [...selected].map(String) });
      notify.success('Deleted.');
      await load();
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message ?? 'Delete failed.';
      notify.error(msg);
    }
  };

  // READ-ONLY columns — legacy ICP_MS_MassPick_ListView order: Method · Matrix ·
  // Parameter · Element · then the four Select/Mass pairs. No inline editing: the
  // mass ticks render as disabled checkboxes, and everything else is plain text.
  const massColumns = useCallback((n: 1 | 2 | 3 | 4): AppColumn<MassPickRow>[] => {
    const flag = `selectMass${n}` as MassFlag;
    const massKey = `mass${n}` as 'mass1' | 'mass2' | 'mass3' | 'mass4';
    return [
      {
        field: flag,
        header: `SelectMass${n}`,
        width: 120,
        align: 'center',
        sortable: false,
        render: (r) => (
          <input type="checkbox" checked={r[flag]} disabled readOnly aria-label={`SelectMass${n} for ${r.parameterName}`} />
        ),
      },
      { field: massKey, header: `Mass${n}`, width: 100, type: 'number' },
    ];
  }, []);

  const columns: AppColumn<MassPickRow>[] = useMemo(() => [
    { field: 'method', header: 'Method', width: 190 },
    { field: 'matrix', header: 'Matrix', width: 170 },
    { field: 'parameterName', header: 'Parameter', width: 200 },
    { field: 'element', header: 'Element', width: 150 },
    ...massColumns(1), ...massColumns(2), ...massColumns(3), ...massColumns(4),
  ], [massColumns]);

  // Export the visible rows via the shared helper — same content the grid's own
  // (now hidden) header Export produced; consumed by the search-row ExportMenu.
  const exportMatrix = useMemo(() => gridExportMatrix(visible, columns), [visible, columns]);

  // ── Detail View — the per-row editor (all editing / adding happens here) ──
  if (mode === 'detail') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div style={{ padding: '10px 16px 0', flex: '0 0 auto' }}>
          <ViewToggle view="detail" onChange={(v) => { if (v === 'list') void closeDetail(); }} />
        </div>
        <IcpMassPickDetail
          key={`${detailRowId ?? 'new'}-${detailKey}`}
          initialRow={detailRowId ? (rows.find((r) => r.id === detailRowId) ?? blankRow(detailKey)) : blankRow(detailKey)}
          initialMode={detailMode}
          parameters={parameters}
          pairs={pairs}
          onSaved={handleDetailSaved}
          onCancel={() => void closeDetail()}
          onDirtyChange={setDetailDirty}
        />
      </div>
    );
  }

  // ── List View (READ-ONLY) ──
  return (
    <div className={`bl-page mlv-host${gridMax ? ' icp-fullgrid' : ''}`}>
      <div className="bl-grid-area">
        {/* Maximize (owner request 2026-09-28, ref: ModuleListView) HIDES the chrome
            (toggle · title · toolbar) and lets the search-row + grid fill the CONTENT
            area (.icp-fullgrid) — the nav stays, and the Export + Restore controls on
            the search row remain visible. Esc or the Restore button un-maximizes. */}
        {!gridMax && (
          <>
            <div style={{ marginBottom: 8 }}>
              <ViewToggle view="list" onChange={(v) => { if (v === 'detail') toggleToDetail(); }} />
            </div>
            <FormTitle pageId={PAGE_ID} loadMs={loadMs} loading={loading} auditFormName="ICP-MS Mass Pick" />
            <AppToolbar
              onNew={openNew}
              onEdit={editSelected}
              editDisabled={selected.size !== 1}
              onDelete={() => void removeSelected()}
              deleteDisabled={selected.size === 0}
              onRefresh={() => void load()}
              refreshing={loading}
            />
          </>
        )}

        {/* Search + Export + Maximize/Restore on ONE row above the grid card — the
            grid's own header tools are hidden (hideHeaderTools). */}
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>✕</span>}
          </div>
          <div className="mlv-grid-acts">
            <ExportMenu iconOnly triggerClassName="dv-gridact" filename="ICP-MS Mass Pick"
              headers={exportMatrix.headers} rows={exportMatrix.rows} />
            <button
              className={`dv-gridact${gridMax ? ' dv-on' : ''}`}
              onClick={() => setGridMax((v) => !v)}
              title={gridMax ? 'Restore' : 'Maximize'}
            >
              <i className={`ti ti-arrows-${gridMax ? 'minimize' : 'maximize'}`} />
            </button>
          </div>
        </div>

        <AppDataGrid<MassPickRow>
          rows={visible}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          selectable
          selectedIds={selected}
          onSelectionChange={setSelected}
          onRowDoubleClick={(r) => openView(r.id)}
          initialSort={{ key: 'parameterName', dir: 'asc' }}
          defaultPageSize={20}
          hideHeaderTools
          emptyText="No mass picks"
        />
      </div>
    </div>
  );
}

export default IcpMassPickPage;
